import React, { useContext, useState } from 'react';

import { RicosContext } from './../../ricos-context';
import { ModalServiceContext } from './../../ricos-context/ModalContext';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { PmNodeType } from './../../_shared/pm-node-types';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { resolveColorsPalette } from './../../wix-rich-content-common';
import { DEFAULT_PALETTE } from './../consts';
import type { ImagePluginEditorConfig } from './../types';
import { NewColorPicker } from './../../ui-components/color-picker/new-color-picker';

interface BorderColorPickerProps {
  modalId: string;
  nodeId: string;
  referenceElement: HTMLDivElement | null;
  defaultColor: string | undefined;
  onColorSelected: (color: string | undefined) => void;
  onColorAdded: (color: string) => void;
  config: ImagePluginEditorConfig;
}

export const BorderColorPicker: React.FC<BorderColorPickerProps> = ({
  modalId,
  nodeId,
  referenceElement,
  config,
  defaultColor,
  onColorSelected,
  onColorAdded: onColorAddedProp,
}) => {
  const modalService = useContext(ModalServiceContext);
  const { t } = useContext(RicosContext);

  const { pmNodeAttrs } = useNodeAttrs(nodeId, PmNodeType.IMAGE);

  const logBi = useBiNext({
    modalId,
    pluginId: 'image',
    nodeId,
  });

  const themeColors = resolveColorsPalette(
    DEFAULT_PALETTE.concat(config?.colors ? Object.values(config?.colors) : []),
    undefined,
    57
  );

  const [customColors, setCustomColors] = useState(
    resolveColorsPalette(
      config.getBorderColors?.() ?? [],
      pmNodeAttrs?.styles?.border?.color ?? defaultColor,
      57
    )
  );

  const onColorAdded = (color: string) => {
    onColorAddedProp(color);
    setCustomColors(prev => [...prev, color]);
    onColorSelected(color);
  };

  const closeModal = () => modalService.closeModal(modalId);

  const onResetColor = () => onColorSelected(undefined);

  const currentColor = pmNodeAttrs?.styles?.border?.color?.toUpperCase();

  return (
    <NewColorPicker
      themeColors={themeColors}
      customColors={customColors}
      currentColor={currentColor}
      title={t('Colours')}
      closeModal={closeModal}
      onColorSelected={onColorSelected}
      onColorAdded={onColorAdded}
      onResetColor={onResetColor}
      referenceElement={referenceElement}
      logBi={logBi}
    />
  );
};
